import { Fragment } from 'react';
import type { ReactElement } from 'react';

import { EMPTY_STRING } from '@e-socna/empty';
import { ensureDefined } from '@e-socna/utils';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';
import type { CivilityId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

import { useMissionQuickAccess } from './hooks/useMissionQuickAccess.hook';

export const useDevisEmetteur = (
  parameters: ResolverParameters,
): ResolverResult<ReactElement> => {
  const { civilitiesIdMap } = useReferencesContext();

  const quickAccess = useMissionQuickAccess(parameters);

  if (!quickAccess.isSuccess) {
    return {
      ...quickAccess,
      data: undefined,
    };
  }

  const { thirdParty } = ensureDefined({
    thirdParty:
      quickAccess.data?.consolidation.quotationEmitterActor.thirdParty,
  });

  if (!thirdParty.billingAddress) {
    return {
      data: undefined,
      error: new Error('Adresse de facturation manquante'),
      isSuccess: false,
      isLoading: false,
    };
  }

  const { letterFormat } = thirdParty.billingAddress;

  const { email, landlineNumber, mobileNumber, professionalNumber } =
    thirdParty.contactInfo ?? {};

  const getName = () => {
    switch (thirdParty.__typename) {
      case 'Organization':
        return thirdParty.name;
      case 'Person': {
        const { civility } = ensureDefined({
          civility: civilitiesIdMap[thirdParty.civilityId as CivilityId],
        });

        return `${civility.label} ${thirdParty.fullName}`;
      }
      default:
        throw new Error('Unknown third party type');
    }
  };

  const content = (
    <div className={RESOLVER_CONTENT_CLASSNAME}>
      <p>
        {getName()}
        <br />
        {letterFormat?.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            {line}
            <br />
          </Fragment>
        ))}
        {email ? (
          <>
            {email}
            <br />
          </>
        ) : (
          EMPTY_STRING
        )}
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        {professionalNumber || landlineNumber || mobileNumber}
      </p>
    </div>
  );

  return {
    data: { generated: content },
    error: null,
    isSuccess: true,
    isLoading: false,
  };
};
